import { useState } from "react";
import { META_MODULE_ENABLED } from "../../lib/features";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FileText,
  Plus,
  ShieldCheck,
  QrCode,
  Trash2,
  Send,
  Sparkles,
  ExternalLink,
  Phone,
  MessageSquare,
  RefreshCw,
  Edit3,
} from "lucide-react";
import {
  listTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  submitMetaTemplate,
  type CurrentUser,
  type TemplateItem,
} from "../../lib/api";

export function TemplatesView({ token, user }: { token: string; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [protocolFilter, setProtocolFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

  // Template Form State
  const [channelType, setChannelType] = useState<"meta_official" | "qr_whatsapp">(META_MODULE_ENABLED ? "meta_official" : "qr_whatsapp");
  const [templateName, setTemplateName] = useState("");
  const [category, setCategory] = useState<"MARKETING" | "UTILITY" | "AUTHENTICATION">("MARKETING");
  const [language, setLanguage] = useState("en");
  const [headerType, setHeaderType] = useState<"none" | "text" | "image" | "document" | "video">("none");
  const [headerText, setHeaderText] = useState("");
  const [bodyText, setBodyText] = useState(
    "Hello {{1}}! Welcome to our exclusive loyalty club. Your reward code is {{2}}."
  );
  const [footerText, setFooterText] = useState("Reply STOP to unsubscribe");
  const [buttons, setButtons] = useState<Array<{ type: "quick_reply" | "url" | "phone"; text: string; value?: string }>>([
    { type: "quick_reply", text: "Claim Discount" },
    { type: "url", text: "Visit Store", value: "https://wamaster.io" },
  ]);

  // Live Templates Query
  const templatesQuery = useQuery({
    queryKey: ["templates", user.tenant_id],
    queryFn: () => listTemplates(token, user.tenant_id),
  });

  const templates = templatesQuery.data || [];

  // Create Template Mutation
  const createMutation = useMutation({
    mutationFn: () =>
      createTemplate(
        token,
        {
          name: templateName.trim(),
          category,
          language,
          structure: {
            channel_type: channelType,
            header_type: headerType,
            header_content: headerText || undefined,
            body_text: bodyText.trim(),
            footer_text: footerText.trim() || undefined,
            buttons: buttons.filter((b) => b.text.trim().length > 0),
          },
        },
        user.tenant_id
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setIsRegisterModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      alert(`Failed to save template: ${err.message || "Unknown error"}`);
    },
  });

  const [editingTemplate, setEditingTemplate] = useState<TemplateItem | null>(null);

  // Update Template Mutation
  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editingTemplate) throw new Error("No template selected");
      return updateTemplate(token, editingTemplate.id, {
        name: templateName.trim(),
        category,
        language,
        structure: {
          channel_type: channelType,
          header_type: headerType,
          header_content: headerText || undefined,
          body_text: bodyText.trim(),
          footer_text: footerText.trim() || undefined,
          buttons: buttons.filter((b) => b.text.trim().length > 0),
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setEditingTemplate(null);
      resetForm();
    },
    onError: (err: any) => {
      alert(`Failed to update template: ${err.message || "Unknown error"}`);
    },
  });

  // Delete Template Mutation
  const deleteMutation = useMutation({
    mutationFn: (templateId: string) => deleteTemplate(token, templateId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["templates"] }),
    onError: (err: any) => {
      alert(`Failed to delete template: ${err.message || "Unknown error"}`);
    },
  });

  // Submit Meta Approval Mutation
  const submitMetaMutation = useMutation({
    mutationFn: (templateId: string) => submitMetaTemplate(token, templateId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["templates"] }),
    onError: (err: any) => {
      alert(`Failed to submit template to Meta: ${err.message || "Unknown error"}`);
    },
  });

  function resetForm() {
    setTemplateName("");
    setChannelType(META_MODULE_ENABLED ? "meta_official" : "qr_whatsapp");
    setCategory("MARKETING");
    setLanguage("en");
    setHeaderType("none");
    setHeaderText("");
    setBodyText("Hello {{1}}! Thank you for choosing our service. Your update is: {{2}}.");
    setFooterText("Reply STOP to unsubscribe");
    setButtons([
      { type: "quick_reply", text: "Track Order" },
      { type: "url", text: "View Details", value: "https://example.com" },
    ]);
  }

  function addVariable() {
    const matches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
    const nextVar = matches.length + 1;
    setBodyText((prev) => prev + ` {{${nextVar}}}`);
  }

  function addButton() {
    if (buttons.length >= 3) return;
    setButtons([...buttons, { type: "quick_reply", text: `Option ${buttons.length + 1}` }]);
  }

  function removeButton(index: number) {
    setButtons(buttons.filter((_, i) => i !== index));
  }

  function updateButton(index: number, field: string, value: string) {
    const next = [...buttons];
    const item = next[index];
    if (item) {
      (item as any)[field] = value;
      setButtons(next);
    }
  }

  const filteredTemplates = templates.filter((t) => {
    if (!META_MODULE_ENABLED && (t.structure?.channel_type || "meta_official") !== "qr_whatsapp") return false;
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "all" || t.category === categoryFilter;
    const matchesProtocol =
      protocolFilter === "all" ||
      (t.structure?.channel_type || "meta_official") === protocolFilter;
    return matchesSearch && matchesCategory && matchesProtocol;
  });

  return (
    <div className="space-y-5">
      {/* 1. Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <FileText className="w-6 h-6 text-emerald-600" />
            Message Templates Studio
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Manage reusable message templates for your linked QR instances.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => templatesQuery.refetch()}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${templatesQuery.isFetching ? "animate-spin" : ""}`} />
            <span>Sync</span>
          </button>

          <button
            onClick={() => {
              resetForm();
              setIsRegisterModalOpen(true);
            }}
            className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Register Template</span>
          </button>
        </div>
      </div>

      {/* 2. Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search templates by name..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Protocol Filter */}
          <select
            value={protocolFilter}
            onChange={(e) => setProtocolFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">⊕ Protocol: All Channels</option>
            {META_MODULE_ENABLED && <option value="meta_official">Meta Verified WABA</option>}
            <option value="qr_whatsapp">Direct QR Multi-Device</option>
          </select>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">⊕ Category: All</option>
            <option value="MARKETING">Marketing</option>
            <option value="UTILITY">Utility / Updates</option>
            <option value="AUTHENTICATION">Authentication / OTP</option>
          </select>
        </div>
      </div>

      {/* 3. Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {templatesQuery.isLoading ? (
          <div className="col-span-3 p-16 text-center text-slate-400 text-xs bg-white border border-slate-200 rounded-xl shadow-2xs">
            <div className="flex flex-col items-center justify-center gap-2">
              <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
              <span className="font-semibold text-slate-600">Loading template catalog...</span>
              <span className="text-[11px] text-slate-400">Fetching message layouts</span>
            </div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="col-span-3 p-12 text-center bg-white border border-slate-200 rounded-xl shadow-2xs space-y-3">
            <FileText className="w-12 h-12 text-slate-300 mx-auto" />
            <h3 className="text-sm font-bold text-slate-900">No message templates registered yet</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Create pre-approved WhatsApp templates with headers, dynamic variables (e.g. <code>{"{{1}}"}</code>), and interactive quick reply buttons.
            </p>
            <button
              onClick={() => {
                resetForm();
                setIsRegisterModalOpen(true);
              }}
              className="btn-primary text-xs py-2 px-3.5 mx-auto inline-flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Create First Template</span>
            </button>
          </div>
        ) : (
          filteredTemplates.map((tmpl: TemplateItem) => {
            const isMeta = (tmpl.structure?.channel_type || "meta_official") === "meta_official";
            const isApproved = tmpl.status === "APPROVED";

            return (
              <div
                key={tmpl.id}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs flex flex-col justify-between hover:border-slate-300 transition-all space-y-4"
              >
                <div className="space-y-3">
                  {/* Top Badges */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-lg ${isMeta ? "bg-blue-50 text-blue-600 border border-blue-200" : "bg-emerald-50 text-emerald-600 border border-emerald-200"}`}>
                        {isMeta ? <ShieldCheck className="w-4 h-4" /> : <QrCode className="w-4 h-4" />}
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 text-xs font-mono">{tmpl.name}</h3>
                        <span className="text-[10px] text-slate-400 font-semibold">{tmpl.category} · {tmpl.language}</span>
                      </div>
                    </div>

                    <span
                      className={`status-pill ${
                        isApproved
                          ? "status-pill--connected"
                          : tmpl.status === "PENDING"
                          ? "status-pill--pending"
                          : "status-pill--disconnected"
                      }`}
                    >
                      {isApproved ? "Approved" : tmpl.status}
                    </span>
                  </div>

                  {/* WhatsApp Chat Bubble Mockup Preview */}
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs space-y-2 relative font-sans">
                    {/* Header */}
                    {tmpl.structure?.header_content && (
                      <div className="font-bold text-slate-900 text-xs pb-1 border-b border-slate-200/60">
                        {tmpl.structure.header_content}
                      </div>
                    )}

                    {/* Body */}
                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {tmpl.structure?.body_text || "Template content"}
                    </p>

                    {/* Footer */}
                    {tmpl.structure?.footer_text && (
                      <p className="text-[10px] text-slate-400 pt-1">
                        {tmpl.structure.footer_text}
                      </p>
                    )}

                    {/* Interactive Buttons */}
                    {tmpl.structure?.buttons && tmpl.structure.buttons.length > 0 && (
                      <div className="pt-2 border-t border-slate-200/80 space-y-1">
                        {tmpl.structure.buttons.map((btn, bIdx) => (
                          <div
                            key={bIdx}
                            className="w-full py-1.5 px-3 rounded bg-white border border-slate-200 text-center font-semibold text-emerald-700 text-[11px] shadow-2xs flex items-center justify-center gap-1"
                          >
                            {btn.type === "url" ? (
                              <ExternalLink className="w-3 h-3" />
                            ) : btn.type === "phone" ? (
                              <Phone className="w-3 h-3" />
                            ) : (
                              <MessageSquare className="w-3 h-3" />
                            )}
                            <span>{btn.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Bottom Actions */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-xs">
                  {isMeta && !isApproved ? (
                    <button
                      onClick={() => submitMetaMutation.mutate(tmpl.id)}
                      disabled={submitMetaMutation.isPending}
                      className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1 shadow-2xs"
                    >
                      <Send className="w-3 h-3" />
                      <span>{submitMetaMutation.isPending ? "Submitting..." : "Submit to Meta"}</span>
                    </button>
                  ) : (
                    <span className="text-[11px] text-slate-400 font-mono">
                      Created: {new Date(tmpl.created_at).toLocaleDateString()}
                    </span>
                  )}

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingTemplate(tmpl);
                        setTemplateName(tmpl.name);
                        setChannelType(tmpl.structure?.channel_type || "meta_official");
                        setCategory(tmpl.category);
                        setLanguage(tmpl.language);
                        setHeaderType(tmpl.structure?.header_type || "none");
                        setHeaderText(tmpl.structure?.header_content || "");
                        setBodyText(tmpl.structure?.body_text || "");
                        setFooterText(tmpl.structure?.footer_text || "");
                        setButtons(tmpl.structure?.buttons || []);
                        setIsRegisterModalOpen(true);
                      }}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                      title="Edit template"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>

                    <button
                      onClick={() => {
                        if (confirm(`Delete template "${tmpl.name}"?`)) {
                          deleteMutation.mutate(tmpl.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                      title="Delete template"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 4. Interactive Template Registration Modal (with Live WhatsApp Phone Mockup) */}
      {isRegisterModalOpen && (
        <div className="modal-backdrop" onClick={() => {
          setIsRegisterModalOpen(false);
          setEditingTemplate(null);
        }}>
          <div className="modal-card max-w-4xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-4">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-emerald-600" />
                {editingTemplate ? "Edit WhatsApp Message Template" : "Register WhatsApp Message Template"}
              </h3>
              <button onClick={() => {
                setIsRegisterModalOpen(false);
                setEditingTemplate(null);
              }} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column: Form Controls (7 Cols) */}
              <div className="lg:col-span-7 space-y-4">
                {/* Protocol Selector */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Channel Protocol *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {META_MODULE_ENABLED && <button
                      type="button"
                      onClick={() => setChannelType("meta_official")}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        channelType === "meta_official"
                          ? "bg-blue-50 border-blue-500 text-blue-900 font-bold"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 text-xs">
                        <ShieldCheck className="w-4 h-4 text-blue-600" />
                        Official Meta WABA
                      </div>
                      <p className="text-[10px] text-slate-500 font-normal mt-0.5">Subject to Meta 24-hr guidelines</p>
                    </button>}

                    <button
                      type="button"
                      onClick={() => setChannelType("qr_whatsapp")}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        channelType === "qr_whatsapp"
                          ? "bg-emerald-50 border-emerald-500 text-emerald-900 font-bold"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 text-xs">
                        <QrCode className="w-4 h-4 text-emerald-600" />
                        Direct QR Node
                      </div>
                      <p className="text-[10px] text-slate-500 font-normal mt-0.5">Instant activation with Spintax</p>
                    </button>
                  </div>
                </div>

                {/* Template Name & Category */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Template Name (snake_case) *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. order_update_v1"
                      className="modern-input w-full font-mono text-xs"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Category *</label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value as any)}
                      className="modern-input w-full text-xs"
                    >
                      <option value="MARKETING">Marketing / Promotions</option>
                      <option value="UTILITY">Utility / Order Updates</option>
                      <option value="AUTHENTICATION">Authentication / OTP</option>
                    </select>
                  </div>
                </div>

                {/* Language */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Language *</label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="modern-input w-full text-xs font-mono"
                  >
                    <option value="en">English (en)</option>
                    <option value="hi">Hindi (hi)</option>
                    <option value="ar">Arabic (ar)</option>
                    <option value="es">Spanish (es)</option>
                    <option value="pt_BR">Portuguese (pt_BR)</option>
                  </select>
                </div>

                {/* Header Option */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Header (Optional)</label>
                  <div className="flex gap-2 mb-1.5">
                    {(["none", "text", "image", "document"] as const).map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setHeaderType(h)}
                        className={`px-2.5 py-1 rounded text-xs font-semibold capitalize border ${
                          headerType === h
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-slate-50 text-slate-600 border-slate-200"
                        }`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                  {headerType === "text" && (
                    <input
                      type="text"
                      placeholder="Header title text..."
                      value={headerText}
                      onChange={(e) => setHeaderText(e.target.value)}
                      className="modern-input w-full text-xs"
                    />
                  )}
                </div>

                {/* Body Text + Variable Inserter */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-slate-700">Message Body *</label>
                    <button
                      type="button"
                      onClick={addVariable}
                      className="text-xs text-emerald-600 font-bold hover:underline flex items-center gap-1"
                    >
                      <Sparkles className="w-3 h-3" />
                      <span>+ Add Variable</span>
                    </button>
                  </div>
                  <textarea
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value)}
                    rows={4}
                    className="modern-input w-full text-xs font-sans"
                    placeholder="Type your message body here..."
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Variables like <code>{"{{1}}"}</code> and <code>{"{{2}}"}</code> will be substituted dynamically during broadcast.
                  </p>
                </div>

                {/* Footer Text */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Footer (Optional)</label>
                  <input
                    type="text"
                    value={footerText}
                    onChange={(e) => setFooterText(e.target.value)}
                    className="modern-input w-full text-xs"
                    placeholder="e.g. Reply STOP to unsubscribe"
                  />
                </div>

                {/* Buttons Builder */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-slate-700">Interactive Buttons (Up to 3)</label>
                    {buttons.length < 3 && (
                      <button
                        type="button"
                        onClick={addButton}
                        className="text-xs text-emerald-600 font-bold hover:underline"
                      >
                        + Add Button
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {buttons.map((btn, bIdx) => (
                      <div key={bIdx} className="flex items-center gap-2">
                        <select
                          value={btn.type}
                          onChange={(e) => updateButton(bIdx, "type", e.target.value)}
                          className="modern-input w-32 text-xs"
                        >
                          <option value="quick_reply">Quick Reply</option>
                          <option value="url">URL Link</option>
                          <option value="phone">Phone Call</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Button Label"
                          value={btn.text}
                          onChange={(e) => updateButton(bIdx, "text", e.target.value)}
                          className="modern-input flex-1 text-xs"
                        />
                        {btn.type === "url" && (
                          <input
                            type="text"
                            placeholder="https://example.com"
                            value={btn.value || ""}
                            onChange={(e) => updateButton(bIdx, "value", e.target.value)}
                            className="modern-input flex-1 text-xs font-mono"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => removeButton(bIdx)}
                          className="p-1 text-slate-400 hover:text-rose-600"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column: Live WhatsApp Phone Mockup (5 Cols) */}
              <div className="lg:col-span-5 flex flex-col items-center justify-start">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                  Live WhatsApp Chat Mockup
                </div>

                {/* Realistic Smartphone Preview */}
                <div className="w-full max-w-[280px] bg-slate-900 rounded-3xl p-3 shadow-xl border-4 border-slate-800">
                  {/* Smartphone Top Notch & Bar */}
                  <div className="h-4 flex items-center justify-between px-2 text-[9px] text-white/80 font-mono mb-2">
                    <span>12:00</span>
                    <div className="w-12 h-2.5 bg-black rounded-full" />
                    <span>5G 100%</span>
                  </div>

                  {/* WhatsApp Chat Area */}
                  <div className="bg-[#EFEAE2] rounded-2xl p-3 min-h-[340px] flex flex-col justify-end space-y-2 relative overflow-hidden">
                    {/* WhatsApp Message Bubble */}
                    <div className="bg-white rounded-xl rounded-bl-xs p-3 shadow-xs space-y-2 text-xs text-slate-900 border border-slate-200">
                      {headerType === "text" && headerText && (
                        <div className="font-bold text-slate-900 text-xs pb-1 border-b border-slate-100">
                          {headerText}
                        </div>
                      )}
                      {headerType === "image" && (
                        <div className="w-full h-24 bg-slate-200 rounded-lg flex items-center justify-center text-slate-500 text-[10px]">
                          [ Image Header ]
                        </div>
                      )}

                      <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-slate-800">
                        {bodyText || "Your message body..."}
                      </p>

                      {footerText && (
                        <p className="text-[9px] text-slate-400 pt-0.5">
                          {footerText}
                        </p>
                      )}

                      <div className="text-[8px] text-right text-slate-400 font-mono">
                        12:00 PM ✓✓
                      </div>
                    </div>

                    {/* Action Buttons Mockup */}
                    {buttons.length > 0 && (
                      <div className="space-y-1">
                        {buttons.map((btn, idx) => (
                          <div
                            key={idx}
                            className="bg-white rounded-lg py-1.5 px-3 text-center font-bold text-emerald-700 text-[10px] shadow-xs border border-slate-200 flex items-center justify-center gap-1"
                          >
                            {btn.type === "url" ? (
                              <ExternalLink className="w-2.5 h-2.5" />
                            ) : btn.type === "phone" ? (
                              <Phone className="w-2.5 h-2.5" />
                            ) : (
                              <MessageSquare className="w-2.5 h-2.5" />
                            )}
                            <span>{btn.text || `Button ${idx + 1}`}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Bottom Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-4">
              <button className="btn-secondary text-xs" onClick={() => {
                setIsRegisterModalOpen(false);
                setEditingTemplate(null);
              }}>
                Cancel
              </button>
              <button
                className="btn-primary text-xs"
                onClick={() => {
                  if (editingTemplate) {
                    updateMutation.mutate();
                  } else {
                    createMutation.mutate();
                  }
                }}
                disabled={!templateName.trim() || !bodyText.trim() || createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : editingTemplate
                  ? "Update Template"
                  : "Save & Register Template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
